/**
 * BlockInspector — Sidebar panel for editing block content and layout.
 *
 * Shows type-specific controls when a block is selected in the preview iframe.
 * Follows the collapsible-groups pattern from SectionConfigPanel.
 *
 * Part of Plan 145 — Block Editor Customizer, Epic 01.
 */

import { useCallback, useState, useRef, useEffect } from 'react'
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react'
import type { BlockContent, BlockType } from '@zaviago/storefront-config'
import type { SelectedBlockInfo } from './useBlockSelection'

const BLOCK_TYPE_LABELS: Partial<Record<BlockType, string>> = {
  text: 'Text',
  image: 'Image',
  button: 'Button',
  divider: 'Divider',
  spacer: 'Spacer',
  icon: 'Icon',
  'search-bar': 'Search Bar',
  'category-nav': 'Category Nav',
  'social-link': 'Social Link',
  'contact-item': 'Contact Item',
  'rich-text': 'Rich Text',
}

interface BlockInspectorProps {
  block: SelectedBlockInfo
  onUpdate: (blockId: string, content: BlockContent) => void
  onWidthChange: (blockId: string, widthPct: number) => void
  onBack: () => void
}

function CollapsibleGroup({
  title,
  defaultOpen = false,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="text-[13px] font-semibold uppercase tracking-wider text-gray-400">
          {title}
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        )}
      </button>
      {open && <div className="space-y-2 px-4 pb-4">{children}</div>}
    </div>
  )
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[13px] font-medium text-gray-600">
      {children}
    </label>
  )
}

function FieldInput({
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  value: string | number
  onChange: (v: string) => void
  type?: string
  placeholder?: string
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
    />
  )
}

function EnumSelect({
  value,
  options,
  onChange,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex-1 rounded-xs px-2 py-1.5 text-xs font-medium transition-colors ${
            value === opt.value
              ? 'bg-sky-50 text-sky-600'
              : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function TextBlockControls({
  content,
  onUpdate,
}: {
  content: Extract<BlockContent, { type: 'text' }>
  onUpdate: (c: BlockContent) => void
}) {
  return (
    <>
      <div>
        <FieldLabel>Content</FieldLabel>
        <textarea
          value={content.value}
          onChange={(e) => onUpdate({ ...content, value: e.target.value })}
          rows={3}
          className="w-full rounded-sm border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <FieldLabel>Font Size</FieldLabel>
          <FieldInput
            type="number"
            value={content.fontSize ?? 16}
            onChange={(v) => onUpdate({ ...content, fontSize: Number(v) || 16 })}
          />
        </div>
        <div>
          <FieldLabel>Font Weight</FieldLabel>
          <EnumSelect
            value={String(content.fontWeight ?? 400)}
            options={[
              { value: '400', label: 'Regular' },
              { value: '600', label: 'Semi' },
              { value: '700', label: 'Bold' },
            ]}
            onChange={(v) => onUpdate({ ...content, fontWeight: Number(v) })}
          />
        </div>
      </div>
      <div>
        <FieldLabel>Color</FieldLabel>
        <FieldInput
          value={content.color ?? '#000000'}
          onChange={(v) => onUpdate({ ...content, color: v })}
          placeholder="#000000"
        />
      </div>
      <div>
        <FieldLabel>Text Align</FieldLabel>
        <EnumSelect
          value={content.textAlign ?? 'left'}
          options={[
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
          ]}
          onChange={(v) => onUpdate({ ...content, textAlign: v as 'left' | 'center' | 'right' })}
        />
      </div>
    </>
  )
}

function ImageBlockControls({
  content,
  onUpdate,
}: {
  content: Extract<BlockContent, { type: 'image' }>
  onUpdate: (c: BlockContent) => void
}) {
  return (
    <>
      <div>
        <FieldLabel>Image URL</FieldLabel>
        <FieldInput
          value={content.src}
          onChange={(v) => onUpdate({ ...content, src: v })}
          placeholder="https://..."
        />
      </div>
      <div>
        <FieldLabel>Alt Text</FieldLabel>
        <FieldInput
          value={content.alt ?? ''}
          onChange={(v) => onUpdate({ ...content, alt: v })}
        />
      </div>
      <div>
        <FieldLabel>Object Fit</FieldLabel>
        <EnumSelect
          value={content.objectFit ?? 'cover'}
          options={[
            { value: 'cover', label: 'Cover' },
            { value: 'contain', label: 'Contain' },
            { value: 'fill', label: 'Fill' },
          ]}
          onChange={(v) => onUpdate({ ...content, objectFit: v as 'cover' | 'contain' | 'fill' })}
        />
      </div>
    </>
  )
}

function ButtonBlockControls({
  content,
  onUpdate,
}: {
  content: Extract<BlockContent, { type: 'button' }>
  onUpdate: (c: BlockContent) => void
}) {
  return (
    <>
      <div>
        <FieldLabel>Label</FieldLabel>
        <FieldInput
          value={content.label}
          onChange={(v) => onUpdate({ ...content, label: v })}
        />
      </div>
      <div>
        <FieldLabel>Link URL</FieldLabel>
        <FieldInput
          value={content.href ?? ''}
          onChange={(v) => onUpdate({ ...content, href: v })}
          placeholder="/collections"
        />
      </div>
      <div>
        <FieldLabel>Style</FieldLabel>
        <EnumSelect
          value={content.variant ?? 'filled'}
          options={[
            { value: 'filled', label: 'Filled' },
            { value: 'outline', label: 'Outline' },
            { value: 'ghost', label: 'Ghost' },
          ]}
          onChange={(v) => onUpdate({ ...content, variant: v as 'filled' | 'outline' | 'ghost' })}
        />
      </div>
      <div>
        <FieldLabel>Size</FieldLabel>
        <EnumSelect
          value={content.size ?? 'md'}
          options={[
            { value: 'sm', label: 'Small' },
            { value: 'md', label: 'Medium' },
            { value: 'lg', label: 'Large' },
          ]}
          onChange={(v) => onUpdate({ ...content, size: v as 'sm' | 'md' | 'lg' })}
        />
      </div>
    </>
  )
}

function DividerBlockControls({
  content,
  onUpdate,
}: {
  content: Extract<BlockContent, { type: 'divider' }>
  onUpdate: (c: BlockContent) => void
}) {
  return (
    <>
      <div>
        <FieldLabel>Color</FieldLabel>
        <FieldInput
          value={content.color ?? '#e5e7eb'}
          onChange={(v) => onUpdate({ ...content, color: v })}
        />
      </div>
      <div>
        <FieldLabel>Thickness</FieldLabel>
        <FieldInput
          type="number"
          value={content.thickness ?? 1}
          onChange={(v) => onUpdate({ ...content, thickness: Number(v) || 1 })}
        />
      </div>
    </>
  )
}

function SpacerBlockControls({
  content,
  onUpdate,
}: {
  content: Extract<BlockContent, { type: 'spacer' }>
  onUpdate: (c: BlockContent) => void
}) {
  return (
    <div>
      <FieldLabel>Height (px)</FieldLabel>
      <FieldInput
        type="number"
        value={content.heightPx}
        onChange={(v) => onUpdate({ ...content, heightPx: Number(v) || 24 })}
      />
    </div>
  )
}

function IconBlockControls({
  content,
  onUpdate,
}: {
  content: Extract<BlockContent, { type: 'icon' }>
  onUpdate: (c: BlockContent) => void
}) {
  return (
    <>
      <div>
        <FieldLabel>Icon Name</FieldLabel>
        <FieldInput
          value={content.name}
          onChange={(v) => onUpdate({ ...content, name: v })}
          placeholder="star"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <FieldLabel>Size</FieldLabel>
          <FieldInput
            type="number"
            value={content.size ?? 24}
            onChange={(v) => onUpdate({ ...content, size: Number(v) || 24 })}
          />
        </div>
        <div>
          <FieldLabel>Color</FieldLabel>
          <FieldInput
            value={content.color ?? ''}
            onChange={(v) => onUpdate({ ...content, color: v })}
          />
        </div>
      </div>
    </>
  )
}

const WIDTH_OPTIONS = [
  { value: '25', label: '25%' },
  { value: '33', label: '33%' },
  { value: '50', label: '50%' },
  { value: '75', label: '75%' },
  { value: '100', label: '100%' },
]

export function BlockInspector({ block, onUpdate, onWidthChange, onBack }: BlockInspectorProps) {
  // Debounce content updates (300ms) to prevent render storms on every keystroke
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const pendingContentRef = useRef<BlockContent | null>(null)

  // Flush pending update on unmount or block change
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        if (pendingContentRef.current) {
          onUpdate(block.blockId, pendingContentRef.current)
          pendingContentRef.current = null
        }
      }
    }
  }, [block.blockId, onUpdate])

  const handleContentUpdate = useCallback(
    (content: BlockContent) => {
      pendingContentRef.current = content
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onUpdate(block.blockId, content)
        pendingContentRef.current = null
      }, 300)
    },
    [block.blockId, onUpdate],
  )

  const blockType = block.content.type as BlockType

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
        <button
          onClick={onBack}
          className="rounded-xs p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold text-gray-900">
          {BLOCK_TYPE_LABELS[blockType] ?? 'Block'}
        </span>
        <span className="rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-600">
          {blockType}
        </span>
      </div>

      {/* Content controls (always visible) */}
      <CollapsibleGroup title="Content" defaultOpen>
        {block.content.type === 'text' && (
          <TextBlockControls content={block.content} onUpdate={handleContentUpdate} />
        )}
        {block.content.type === 'image' && (
          <ImageBlockControls content={block.content} onUpdate={handleContentUpdate} />
        )}
        {block.content.type === 'button' && (
          <ButtonBlockControls content={block.content} onUpdate={handleContentUpdate} />
        )}
        {block.content.type === 'divider' && (
          <DividerBlockControls content={block.content} onUpdate={handleContentUpdate} />
        )}
        {block.content.type === 'spacer' && (
          <SpacerBlockControls content={block.content} onUpdate={handleContentUpdate} />
        )}
        {block.content.type === 'icon' && (
          <IconBlockControls content={block.content} onUpdate={handleContentUpdate} />
        )}
      </CollapsibleGroup>

      {/* Layout controls */}
      <CollapsibleGroup title="Layout" defaultOpen>
        <div>
          <FieldLabel>Width</FieldLabel>
          <EnumSelect
            value={String(block.widthPct ?? 100)}
            options={WIDTH_OPTIONS}
            onChange={(v) => onWidthChange(block.blockId, Number(v))}
          />
        </div>
      </CollapsibleGroup>
    </div>
  )
}
